import React, { useEffect } from "react";
import { connect } from "react-redux";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { bindActionCreators, Dispatch } from "@reduxjs/toolkit";

import { ApplicationState } from "../store";
import { doLogoutApp, refreshToken, doLogoutAppExit } from "../store/ducks/auth/actions";
import {
  canAccessAdminPanel,
  canAccessStudentPortal,
  getDefaultAuthenticatedPath,
} from "../config/auth/accessControl";

import ContentListingPage from "../Pages/PortalAluno/ContentListing/ContentListingPage";
import ContentDetailsPage from "../Pages/PortalAluno/ContentDetailsPage";
import EvaluationPage from "../Pages/PortalAluno/EvaluationPage";
import FavoritesPage from "../Pages/PortalAluno/Favorites/FavoritesPage";
import SearchResultsPage from "../Pages/PortalAluno/Search/SearchResultsPage";
import HomePage from "../Pages/PortalAluno/Home/HomePage";
import LibraryPage from "../Pages/PortalAluno/Library/LibraryPage";
import ChatbotContentRedirect from "../Pages/PortalAluno/Chatbot/ChatbotContentRedirect";
import AppLinkRedirect from "../Pages/PortalAluno/SharedLink/AppLinkRedirect";
import { APP_LINK_TYPES } from "../utils/portalAluno/appLinkRoutes";
import NotificationsPage from "../Pages/PortalAluno/Notifications/NotificationsPage";
import SurveysPage from "../Pages/PortalAluno/Surveys/SurveysPage";
import ProfilePage from "../Pages/PortalAluno/Profile/ProfilePage";
import HistoryPage from "../Pages/PortalAluno/History/HistoryPage";
import FaqPage from "../Pages/PortalAluno/Faq/FaqPage";
import ContactPage from "../Pages/PortalAluno/Contact/ContactPage";
import DrawerMenu from "../Components/DrawerMenu/DrawerMenu";
import AppLayout from "../Components/AppLayout/AppLayout";
import Usuario from "../Pages/Usuario/Usuario";
import Perfil from "../Pages/Perfil/Perfil";
import Conteudo from "../Pages/Conteudo/Conteudo";
import Avaliacao from "../Pages/Avaliacao/Avaliacao";
import Tema from "../Pages/Tema/Tema";
import Curso from "../Pages/Curso/Curso";
import PesquisaExterna from "../Pages/PesquisaExterna/PesquisaExterna";
import Trilha from "../Pages/Trilha/Trilha";
import Relatorio from "../Pages/Relatorio/Relatorio";
import Treinamento1Ponto from "../Pages/Treinamento1Ponto/Treinamento1Ponto";
import Comunicado from "../Pages/Comunicado/Comunicado";
import Destaque from "../Pages/Destaque/Destaque";
import Justificativa from "../Pages/Justificativa/Justificativa";
import Cluster from "../Pages/Cluster/Cluster";
import Subcluster from "../Pages/Subcluster/Subcluster";
import Grupo from "../Pages/Grupo/Grupo";
import Story from "../Pages/Story/Story";
import Municipio from "../Pages/Municipio/Municipio";
import SegurancaNoTrabalho from "../Pages/SegurancaNoTrabalho/SegurancaNoTrabalho";
import Categoria from "../Pages/Chatbot/Categoria/Categoria";
import Nivel from "../Pages/Chatbot/Nivel/Nivel";
import Saudacao from "../Pages/Chatbot/Saudacao/Saudacao";
import Diagrama from "../Pages/Chatbot/Diagrama/Diagrama";
import Agenda from "../Pages/AgendaNotificacao/AgendaNotificacao";
import Links from "../Pages/Links/Links"
import ExternalRedirect from "../Pages/ExternalRedirect";
import PingIdentify from "../Pages/PingIdentify";
import PingIdentifyApp from "../Pages/PingIdentify/app";
import LoginRedirect from "./LoginRedirect";

import { setAccessToken, setRefreshToken } from "../config/cookies";

export const PING_CONFIG = {
  clientId: process.env.REACT_APP_PING_CLIENT_ID,
  authUrl: process.env.REACT_APP_PING_AUTH_URL,
  redirectUri: `${ process.env.REACT_APP_API_PING_LOCAL || window.location.origin}/auth/callback`,
  logoutAuth: process.env.REACT_APP_PING_LOGOUT_URL,
};

export const AppRouter = ({ auth, doLogoutApp, doLogoutAppExit, refreshToken }: any) => {
  const authData = auth?.data;
  const authorized = Boolean(authData?.authorized);
  const isTreinamentoTecnico = authData?.idPerfil === 30;
  const hasStudentPortalAccess = canAccessStudentPortal(authData);
  const hasAdminPanelAccess = canAccessAdminPanel(authData);
  const defaultAuthenticatedPath = getDefaultAuthenticatedPath(authData);
  const accessToken = authData?.accessToken;
  const chaveToken = authData?.chaveToken;

  const pingUrlCallback = `${PING_CONFIG.authUrl}?response_type=code&client_id=${PING_CONFIG.clientId}&scope=openid&redirect_uri=${PING_CONFIG.redirectUri}`;
  
  useEffect(() => {
    if (!authorized) return;

    if (accessToken) setAccessToken(accessToken);
    if (chaveToken) setRefreshToken(chaveToken);
  }, [accessToken, authorized, chaveToken]);

  return (
    <Router>
      <Routes>
        <Route
          path="/conteudo/:encodedContentId"
          element={<ChatbotContentRedirect />}
        />
        {APP_LINK_TYPES.map((type) => (
          <Route
            key={type}
            path={`/${type}/:encodedId`}
            element={<AppLinkRedirect type={type} />}
          />
        ))}
        <Route
          path="/auth/px/callback"
          element={
            authorized ? (
              <Navigate replace to={defaultAuthenticatedPath} />
            ) : (
              <PingIdentifyApp />
            )
          }
        />
        <Route
          path="/auth/callback"
          element={
            authorized ? (
              <Navigate replace to={defaultAuthenticatedPath} />
            ) : (
              <PingIdentify authorized={false} />
            )
          }
        />
        <Route
          path="/exit"
          element={
            <ExternalRedirect
              url={PING_CONFIG?.logoutAuth || ""}
              doLogoutAppExit={doLogoutAppExit}
              exit={true}
            />
          }
        />
        {authorized ? (
          <>
            {hasStudentPortalAccess && (
              <Route path="/app" element={<AppLayout />}>
                <Route
                  index
                  element={<HomePage />}
                />
              <Route
                path="conteudo"
                element={<Navigate replace to="/app" />}
              />
              <Route
                path="conteudo/:contentType"
                element={<ContentListingPage />}
              />
              <Route
                path="conteudo/detalhes/:contentType/:contentId"
                element={<ContentDetailsPage />}
              />
              <Route
                path="conteudo/detalhes/:contentType/:contentId/avaliacao/:evaluationId"
                element={<EvaluationPage />}
              />
              <Route
                path="conteudo/detalhes/:contentId"
                element={<ContentDetailsPage />}
              />
              <Route
                path="conteudo/detalhes/:contentId/avaliacao/:evaluationId"
                element={<EvaluationPage />}
              />
              <Route
                path="chatbot"
                element={<React.Fragment />}
              />
              <Route path="biblioteca" element={<LibraryPage />} />
              <Route path="biblioteca/:libraryType" element={<LibraryPage />} />
              <Route path="busca" element={<SearchResultsPage />} />
              <Route path="favoritos" element={<FavoritesPage />} />
              <Route path="menu/notificacoes" element={<NotificationsPage />} />
              <Route path="menu/enquetes" element={<SurveysPage />} />
              <Route
                path="menu/enquetes/avaliacao/:evaluationId"
                element={<EvaluationPage />}
              />
              <Route path="menu/meus-dados" element={<ProfilePage />} />
              <Route path="menu/historico" element={<HistoryPage />} />
              <Route path="menu/faq" element={<FaqPage />} />
              <Route path="menu/fale-conosco" element={<ContactPage />} />
              </Route>
            )}
            {hasAdminPanelAccess && (
              <Route
                path="/"
                element={
                  <DrawerMenu
                    {...authData}
                    canAccessStudentPortal={hasStudentPortalAccess}
                    logoutApp={doLogoutApp}
                    refreshToken={refreshToken}
                  />
                }
              >
                <Route
                  index
                  element={<Navigate replace to={defaultAuthenticatedPath} />}
                />
                <Route path="/usuarios" element={<Usuario />} />
                <Route path="/relatorios" element={<Relatorio />} />
                {!isTreinamentoTecnico && (
                  <>
                    <Route path="/perfis" element={<Perfil />} />
                    <Route path="/conteudos" element={<Conteudo />} />
                    <Route path="/destaques" element={<Destaque />} />
                    <Route path="/avaliacoes" element={<Avaliacao />} />
                    <Route path="/pesquisa" element={<PesquisaExterna />} />
                    <Route path="/temas" element={<Tema />} />
                    <Route path="/cursos" element={<Curso />} />
                    <Route path="/segurancaDoTrabalho" element={<SegurancaNoTrabalho />} />
                    <Route path="/trilhas" element={<Trilha />} />
                    <Route path="/t1" element={<Treinamento1Ponto />} />
                    <Route path="/comunicados" element={<Comunicado />} />
                    <Route path="/clusters" element={<Cluster />} />
                    <Route path="/subclusters" element={<Subcluster />} />
                    <Route path="/grupos" element={<Grupo />} />
                    <Route path="/agendaNotificacao" element={<Agenda />} />
                    <Route path="/links" element={<Links/>}/>
                    <Route path="/municipios" element={<Municipio />} />
                    <Route path="/chatbot/categoria" element={<Categoria />} />
                    <Route path="/chatbot/nivel" element={<Nivel />} />
                    <Route path="/chatbot/saudacao" element={<Saudacao />} />
                    <Route path="/chatbot/diagrama" element={<Diagrama />} />
                  </>
                )}
                <Route path="/justificativas" element={<Justificativa />} />
                <Route path="/story" element={<Story />} />
              </Route>
            )}
            <Route
              path="*"
              element={<Navigate replace to={defaultAuthenticatedPath} />}
            />
          </>
        ) : (
        <>
            <Route path="/auth" element={<ExternalRedirect url={pingUrlCallback}/>} />
            <Route path="*" element={<LoginRedirect />} />
        </>
        )}
      </Routes>
    </Router>
  );
};

const mapStateToProps = (state: ApplicationState) => ({
  state: state,
  auth: state.auth,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ doLogoutApp, doLogoutAppExit, refreshToken }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AppRouter);
